import type { AuditEventDTO } from './activity';
import type { Id, ISODate, Page, Snapshot, Version } from './common';

export type TaskStatus = 'not_started' | 'in_progress' | 'completed';
/** The product's word for a task status (web `labels.ts`, the Board, and Hana's prompt read this one table). */
export const TASK_STATUS_LABEL: Readonly<Record<TaskStatus, string>> = {
  not_started: 'Chưa bắt đầu', in_progress: 'Đang làm', completed: 'Đã xong',
};
export type Priority = 'low' | 'normal' | 'high' | 'urgent';

/**
 * Exactly one primary recipient: an internal user (spec §5.2). Outside parties are partners now, named on a
 * card as its stakeholders (projects & stakeholders spec §4.1), never as its recipient.
 */
export type Recipient = { kind: 'user'; userId: Id };

/** At most one direct strategy link: an annual objective or a quarterly priority (spec §7). */
export type StrategyLink = { kind: 'objective' | 'priority'; id: Id } | null;

/**
 * The part of a task the transition rules own. `openCycle` counts how many times the task has been opened
 * (a reopen starts a new reminder cycle) and `blockedSeq` how many times the blocked flag has been switched
 * on; both are markers of notification event keys and never move backwards.
 */
export type TaskState = {
  status: TaskStatus; blocked: boolean; blocker: string | null; openCycle: number; blockedSeq: number;
};

/** What one save may ask of the state. Absent fields mean "leave as is". */
export type TaskTransition = { status?: TaskStatus; blocked?: boolean; blocker?: string | null };

export type TaskRecord = TaskState & {
  id: Id; companyId: Id; title: string; description: string | null;
  recipient: Recipient; deadline: ISODate; priority: Priority; strategyLink: StrategyLink;
  assignmentSeq: number; deadlineSeq: number; version: number; createdAt: string; deletedAt: string | null;
};

/** The four Điều hành lists of spec §5.1, evaluated against one business day. */
export type TaskListFlags = { today: boolean; blocked: boolean; soon: boolean; important: boolean };

// Task 8 -------------------------------------------------------------------------------------------

/** How one save names the recipient: an internal user. */
export type RecipientInput = { kind: 'user'; userId: Id };

/** Everything one create save may ask for. `draftId` ties the in-tab draft to its upload sessions (§9.1). */
export type TaskCreate = {
  title: string; description?: string | null; recipient: RecipientInput; deadline: ISODate;
  priority?: Priority; strategyLink?: StrategyLink; uploadSessionIds?: Id[]; draftId: Id;
};

/**
 * Mutable fields plus the state command. No company, actor or notification counter is ever accepted.
 * `uploadSessionIds` adds files to the task the save is already touching; removing one is its own delete.
 */
export type TaskPatch = TaskTransition & {
  version: Version; title?: string; description?: string | null; recipient?: RecipientInput;
  deadline?: ISODate; priority?: Priority; strategyLink?: StrategyLink; uploadSessionIds?: Id[];
};

/** A comment may be text, files, or both — but never neither (spec §5.5). */
export type CommentCreate = { text: string; draftId: Id; uploadSessionIds?: Id[] };
export type CommentPatch = { version: Version; text: string; uploadSessionIds?: Id[] };

/**
 * Which parent a file is being added to or read from; drafts exist only in the tab until the first save. `ingest`
 * (S5 §7.1) is a document Hana reads: it names no parent, never becomes an attachment, and is the one target that
 * accepts a CSV.
 */
export type FileTarget = { kind: 'task-draft'; draftId: Id } | { kind: 'comment-draft'; draftId: Id; itemId: Id } |
  { kind: 'task'; itemId: Id } | { kind: 'comment'; itemId: Id; commentId: Id } | { kind: 'ingest' };

/**
 * One intended file, from the moment the browser asks for somewhere to put it until it is bound to a task
 * or comment. `size` is the verified byte count and stays null while nothing has been checked; the storage
 * key, the object version and the checksum are server-only and never appear in this shape.
 */
export type UploadState = 'pending' | 'verified' | 'attached' | 'expired';
export type UploadDTO = {
  id: Id; state: UploadState; target: FileTarget; expiresAt: string;
  filename: string; type: string; size: number | null; version: Version;
};

/** The session plus a short-lived POST policy for it; the policy is never cached or replayed. */
export type UploadCreatedDTO = {
  session: UploadDTO; upload: { url: string; fields: Record<string, string> } | null;
};

/**
 * The types the file viewer shows in place (hotfix 2026-09-26): the two images and PDF of the six an upload may be.
 * Only these are ever handed out `inline`; a text or an Office file always downloads.
 */
export const PREVIEWABLE_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'application/pdf'];

export type AttachmentDTO = {
  id: Id; itemId: Id; commentId: Id | null; uploaderId: Id; filename: string;
  mediaType: string; size: number; version: Version; createdAt: string; deletedAt: string | null;
  downloadable: boolean; canDelete: boolean;
};

export type CommentDTO = {
  id: Id; itemId: Id; authorId: Id; author: Snapshot; text: string | null;
  version: Version; createdAt: string; updatedAt: string; deletedAt: string | null; attachments: AttachmentDTO[];
  capabilities: { canEdit: boolean; canDelete: boolean; canAttach: boolean };
};

/** One entry of a task's timeline: an audit event of the task itself, or a current comment (spec §5.5). */
export type TimelineEntryDTO =
  | { kind: 'audit'; id: Id; occurredAt: string; event: AuditEventDTO }
  | { kind: 'comment'; id: Id; occurredAt: string; comment: CommentDTO };

/**
 * A snapshot plus how the referenced row stands *now*, so the current page can label “Đã xóa”/“Vô hiệu hóa”
 * without rewriting the frozen snapshots audit keeps (spec §11.1).
 */
export type ReferenceSnapshot = Snapshot & {
  deleted: boolean; inactive: boolean;
  /**
   * RBAC spec §5.3: the reader may not see what this points at. `name` is then empty and the page shows a
   * restricted label instead, never a link.
   */
  restricted?: true;
};

/**
 * `day` is the server's business day at the moment of the read — the same day `/api/dashboard` and
 * `/api/workplace/items` answer with — so the header judges "quá hạn" by the rule the lists use (`isOverdue` in
 * `@domain/task-lists`) and never by the browser's own calendar.
 */
export type TaskDetailDTO = {
  task: TaskRecord; recipientSnapshot: ReferenceSnapshot; strategySnapshot: ReferenceSnapshot | null;
  attachments: AttachmentDTO[]; comments: Page<CommentDTO>;
  capabilities: { canEdit: boolean; canDelete: boolean; canComment: boolean; canAttach: boolean };
  day: ISODate;
};
